import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { accountQueryKey, type Account } from '@/api/useAccount';
import { putWithRetry } from '@/lib/blobUpload';

export type UpdateCompanyProfileBody = components['schemas']['UpdateCompanyProfileRequest'];
export type UpdateBusinessHoursBody = components['schemas']['UpdateBusinessHoursRequest'];
export type SettingsChange = components['schemas']['AccountSettingsChangeResponse'];

export const settingsChangesQueryKey = ['account', 'settings-changes'] as const;

/** The two types the API accepts for a logo (M2-19). Checked here only to say so early; the API judges the bytes. */
export const LOGO_TYPES = ['image/png', 'image/jpeg'] as const;
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

/**
 * A refusal the API explained in a sentence: a 400's first error, or a 409's
 * body. Shown as it comes — the server's words, not a paraphrase.
 */
export class CompanySettingsRefusedError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly field?: string,
  ) {
    super(message);
    this.name = 'CompanySettingsRefusedError';
  }
}

function refusal(status: number, error: unknown): Error {
  if (status === 409 && typeof error === 'string') {
    return new CompanySettingsRefusedError(error, status);
  }
  const errors = (error as { errors?: Record<string, string[]> } | undefined)?.errors;
  const first = errors ? Object.entries(errors)[0] : undefined;
  if (status === 400 && first?.[1]?.[0]) {
    return new CompanySettingsRefusedError(first[1][0], status, first[0]);
  }
  return new Error(`Company settings request failed: ${status}`);
}

/**
 * Every settings write answers with the account as `GET /api/v1/account`
 * returns it, so the answer goes straight into that cache entry — the header,
 * the documents and the dispatch board all read it, and with a ten-minute
 * staleTime they would otherwise keep the old name, logo or hours for that
 * long. The trail is invalidated too, so "Recent changes" shows the write.
 *
 * ⚠️ `setQueryData`, not only `invalidateQueries`: the invalidation refetches
 * ACTIVE queries, and the board or an estimate open in this tab is not active
 * while Settings is. The set covers them; the invalidation is for any other
 * `['account', …]` entry.
 */
function useApplyAccount() {
  const queryClient = useQueryClient();
  return (account: Account) => {
    queryClient.setQueryData(accountQueryKey, account);
    void queryClient.invalidateQueries({ queryKey: settingsChangesQueryKey });
  };
}

export function useUpdateCompanyProfile() {
  const apply = useApplyAccount();
  return useMutation({
    mutationFn: async (body: UpdateCompanyProfileBody) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/account/profile', { body });
      if (!response.ok || !data) throw refusal(response.status, error);
      return data;
    },
    onSuccess: apply,
  });
}

export function useUpdateBusinessHours() {
  const apply = useApplyAccount();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateBusinessHoursBody) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/account/hours', { body });
      if (!response.ok || !data) throw refusal(response.status, error);
      return data;
    },
    onSuccess: (account) => {
      apply(account);
      // The board's grid and the dashboard's days are drawn from the hours and
      // the zone; their own rows were judged on the old clock (M2-17 groups by
      // the server's dates), so they are re-read rather than re-drawn.
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
      // The board's jobs and the dashboard (M2-17) both sit under this key.
      void queryClient.invalidateQueries({ queryKey: ['jobs', 'board'] });
      // Aging and the date filters are judged on the account's clock (M2-12).
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * The account's payment-reminder steps (M2-16): `PUT /account/invoice-reminders`
 * with the whole set — each one of −3, 7, 14 and 30; empty turns reminders
 * off. Answers with the account, applied like every settings write.
 *
 * ⚠️ Every invoice query is invalidated too: each invoice's `nextReminderOn`
 * is worked out from these steps, and an invoice page left in the cache
 * would otherwise name a reminder the account no longer sends for 30s.
 */
export function useUpdateInvoiceReminders() {
  const apply = useApplyAccount();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (offsets: number[]) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/account/invoice-reminders', {
        body: { offsets },
      });
      if (!response.ok || !data) throw refusal(response.status, error);
      return data;
    },
    onSuccess: (account) => {
      apply(account);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export type UpdateAppointmentEmailSettingsBody = {
  confirmationsEnabled: boolean;
  remindersEnabled: boolean;
  /** A whole hour, `"HH:00"`, on each property's own clock. */
  reminderTime: string;
  /** 0 (the start time alone), 60, 120, 180 or 240. */
  arrivalWindowMinutes: number;
};

/**
 * The account's appointment-email settings (M2-21, Kyle's 7A):
 * `PUT /account/appointment-emails`, replaced whole — the two switches, the
 * reminder hour and the arrival window. Answers with the account, applied like
 * every settings write. Nothing else on screen is worked out from these (the
 * API reads them afresh at every send), so the account entry is all that moves.
 */
export function useUpdateAppointmentEmailSettings() {
  const apply = useApplyAccount();
  return useMutation({
    mutationFn: async (body: UpdateAppointmentEmailSettingsBody) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/account/appointment-emails', { body });
      if (!response.ok || !data) throw refusal(response.status, error);
      return data;
    },
    onSuccess: apply,
  });
}

export function useRemoveLogo() {
  const apply = useApplyAccount();
  return useMutation({
    mutationFn: async () => {
      const { data, response } = await apiClient.DELETE('/api/v1/account/logo');
      if (!response.ok || !data) throw new Error(`Logo removal failed: ${response.status}`);
      return data;
    },
    onSuccess: apply,
  });
}

/** The newest twenty settings changes (Kyle's Q6 = B). Explicit staleTime, so a test at the helper's 0 cannot hide a missing invalidation. */
export function useSettingsChanges() {
  return useQuery({
    queryKey: settingsChangesQueryKey,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/account/settings-changes');
      if (!response.ok || !data) throw new Error(`GET settings-changes failed: ${response.status}`);
      return data;
    },
    staleTime: 10 * 60_000,
  });
}

export type LogoFailure = 'unsupported-type' | 'too-large' | 'storage-unavailable' | 'network' | 'refused' | 'unknown';

/**
 * Request → PUT → complete, the vehicle photo's shape (M1-44), with the
 * logo's rules. Each step has its own `catch`, so only the PUT can claim a
 * network failure (web/CONVENTIONS.md, M1-44). A refusal of the bytes
 * (`/complete`'s 400) carries the server's sentence.
 */
export function useUploadLogo() {
  const apply = useApplyAccount();
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'failed'>('idle');
  const [failure, setFailure] = useState<LogoFailure | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const inFlight = useRef(false);

  function fail(reason: LogoFailure, sentence: string | null = null) {
    setPhase('failed');
    setFailure(reason);
    setMessage(sentence);
  }

  async function upload(file: File): Promise<boolean> {
    if (inFlight.current) return false;
    inFlight.current = true;
    try {
      setFailure(null);
      setMessage(null);
      if (!(LOGO_TYPES as readonly string[]).includes(file.type)) {
        fail('unsupported-type');
        return false;
      }
      if (file.size > LOGO_MAX_BYTES) {
        fail('too-large');
        return false;
      }
      setPhase('uploading');

      let slot: components['schemas']['LogoUploadUrlResponse'];
      try {
        const requested = await apiClient.POST('/api/v1/account/logo', {
          body: { contentType: file.type, sizeBytes: file.size },
        });
        if (!requested.response.ok || !requested.data) {
          if (requested.response.status === 503) fail('storage-unavailable');
          else if (requested.response.status === 400) {
            const err = refusal(400, requested.error);
            fail('refused', err.message);
          } else fail('unknown');
          return false;
        }
        slot = requested.data;
      } catch {
        fail('unknown');
        return false;
      }

      try {
        await putWithRetry(slot.uploadUrl, file);
      } catch {
        fail('network');
        return false;
      }

      try {
        const completed = await apiClient.POST('/api/v1/account/logo/complete', {
          body: { uploadId: slot.uploadId },
        });
        if (!completed.response.ok || !completed.data) {
          const status = completed.response.status;
          if (status === 400 || status === 409) {
            fail('refused', refusal(status, completed.error).message);
          } else fail('unknown');
          return false;
        }
        apply(completed.data);
        setPhase('idle');
        return true;
      } catch {
        fail('unknown');
        return false;
      }
    } finally {
      inFlight.current = false;
    }
  }

  function reset() {
    setPhase('idle');
    setFailure(null);
    setMessage(null);
  }

  return { upload, phase, failure, message, reset };
}
